<template>
  <div class="lp-daterangepicker">
    <div v-if="preview" class="lp-daterangepicker-preview">
      <div class="lp-daterangepicker-preview-label">{{ labelTop }}</div>
      <lp-datepicker
        v-model="startValue"
        :label-top="
          !singleValue
            ? t('LpDateRangePicker.SelectStartDate')
            : t('LpDateRangePicker.SelectDate')
        "
        :input-format="format"
        :typeable="typeable"
        :preview="true"
        data-ua-unique-id="ua-daterangepicker-start"
      />
      {{ previewSeparator }}
      <lp-datepicker
        v-if="!singleValue"
        v-model="endValue"
        label-top="Select end date"
        :input-format="format"
        :typeable="typeable"
        :preview="true"
        data-ua-unique-id="ua-daterangepicker-end"
      />
    </div>
    <lp-select
      v-else
      v-model="selected"
      :options="optionsUpdated"
      :label-by="labelBy"
      :track-by="trackBy"
      placement="bottom"
      class="w-100"
      :label-top="labelTop"
      :placeholder="placeholder"
      :status="status"
      :message="message"
      :search="searchable"
      :required="required"
      :disabled="disabled"
      data-ua-unique-id="ua-daterangepicker-select"
      @update:model-value="updateSelection"
    />

    <lp-modal
      v-model="modal"
      :container="modalContainer"
      size="sm"
      data-ua-unique-id="ua-daterangepicker-modal"
      @modal-close="onCancel"
    >
      <template #header>
        <h3 style="margin: 0">{{ t('LpDateRangePicker.DateRange') }}</h3>
      </template>
      <template #body>
        <lp-datepicker
          v-model="startValue"
          :label-top="
            !singleValue
              ? t('LpDateRangePicker.SelectStartDate')
              : t('LpDateRangePicker.SelectDate')
          "
          :input-format="format"
          :typeable="typeable"
          data-ua-unique-id="ua-daterangepicker-start"
        />
        <lp-datepicker
          v-if="!singleValue"
          v-model="endValue"
          label-top="Select end date"
          :input-format="format"
          :typeable="typeable"
          data-ua-unique-id="ua-daterangepicker-end"
        />
        <div v-if="validationMessage" class="mt-1 text-red">
          {{ validationMessage }}
        </div>
      </template>
      <template #actions>
        <lp-button
          data-ua-unique-id="ua-daterangepicker-cancel"
          @click="onCancel"
        >
          {{ t('Shared.Cancel') }}
        </lp-button>
        <lp-button
          type="primary"
          data-ua-unique-id="ua-daterangepicker-confirm"
          @click="updateRange"
        >
          {{ t('Shared.Confirm') }}
        </lp-button>
      </template>
    </lp-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useRange } from '@/helpers/useRange'
import { format as dateFormat } from 'date-fns'
import { t } from '@/plugins/i18n'

defineOptions({
  name: 'lp-daterangepicker',
})

type Props = {
  modelValue: string | Date | null
  start?: Date | null
  end?: Date | null
  labelTop?: string
  labelBy?: string
  trackBy?: string
  placeholder?: string
  status?: string
  message?: string
  customRangeLabel?: string
  options?: []
  format?: string
  singleValue?: boolean
  searchable?: boolean
  typeable?: boolean
  required?: boolean
  disabled?: boolean
  modalContainer?: string
  preview?: boolean
  previewSeparator?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  start: null,
  end: null,
  labelTop: '',
  labelBy: 'name',
  trackBy: 'value',
  placeholder: 'Select option...',
  status: 'neutral',
  message: '',
  customRangeLabel: 'Custom Range',
  options: () => [],
  format: 'MM/dd/yyyy',
  singleValue: false,
  searchable: false,
  typeable: false,
  required: false,
  disabled: false,
  modalContainer: 'body',
  preview: false,
  previewSeparator: '',
})
const emit = defineEmits(['update-range', 'update-selection'])

const customRangeOption = computed(() => {
  let label = props.customRangeLabel
  if (startValue.value && endValue.value) {
    if (
      dateFormat(startValue.value, props.format) ===
      dateFormat(endValue.value, props.format)
    ) {
      label += ` (${dateFormat(startValue.value, props.format)})`
    } else if (startValue.value && endValue.value) {
      label += ` (${dateFormat(startValue.value, props.format)} - ${dateFormat(
        endValue.value,
        props.format,
      )})`
    }
  } else if (!startValue.value && endValue.value) {
    label += ` (before ${dateFormat(endValue.value, props.format)})`
  } else if (startValue.value && !endValue.value) {
    props.singleValue
      ? (label = `Custom Date (${dateFormat(startValue.value, props.format)})`)
      : (label += ` (after ${dateFormat(startValue.value, props.format)})`)
  }

  return { [props.labelBy]: label, [props.trackBy]: CUSTOM_RANGE }
})

const optionsUpdated = computed(() => {
  return [...props.options, customRangeOption.value]
})

const {
  CUSTOM_RANGE,
  selected,
  modal,
  updateSelection,
  updateRange,
  startValue,
  endValue,
  validationMessage,
  onCancel,
} = useRange(props, emit)

watchEffect(() => {
  if (props.modelValue) {
    selected.value = props.modelValue
  }
  if (props.start || props.end) {
    startValue.value = props.start
    endValue.value = props.end
  }
})
</script>
